import { apiFetch } from './client'

/**
 * Fas 4: publicering. Idag bara TikTok Direct Post. Speglar PublishEndpoints.
 *
 * ⚠️ Enum-VÄRDEN är PascalCase (samma som resten av API:t). privacyLevel är dock
 * TikToks egna KONSTANTER (SELF_ONLY, PUBLIC_TO_EVERYONE …) — de kommer från
 * creator_info och skickas tillbaka oförändrade.
 */
export interface CreatorInfo {
  nickname: string
  username: string
  /** Bara dessa synlighetsnivåer får väljas. En ogranskad app får ofta bara SELF_ONLY. */
  privacyLevelOptions: string[]
  commentDisabled: boolean
  duetDisabled: boolean
  stitchDisabled: boolean
  maxVideoDurationSec: number
}

export interface TikTokPublishRequest {
  accountId: string
  generatedContentId: string
  mediaAssetId: string
  privacyLevel: string
  disableComment: boolean
  disableDuet: boolean
  disableStitch: boolean
  brandOrganicToggle: boolean
  brandContentToggle: boolean
  ownerDeclaredAi: boolean
  musicUsageConfirmed: boolean
  consent: boolean
}

export interface PublishResult {
  scheduledPostId: string
  publishId: string
  status: string
  isAigc: boolean
}

export interface PublishStatus {
  status: string
  rawStatus: string
  failReason: string | null
}

export const publishApi = {
  tiktokCreatorInfo: (accountId: string) =>
    apiFetch<CreatorInfo>('/api/publish/tiktok/creator-info', {
      method: 'POST',
      body: { accountId },
    }),

  publishTikTok: (req: TikTokPublishRequest) =>
    apiFetch<PublishResult>('/api/publish/tiktok', { method: 'POST', body: req }),

  status: (scheduledPostId: string) =>
    apiFetch<PublishStatus>(`/api/publish/${scheduledPostId}/status`),
}

// ---- presentation ----

/** TikToks synlighetskonstanter → svensk etikett. Färg får aldrig vara ensam bärare. */
export const PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Alla (offentligt)',
  MUTUAL_FOLLOW_FRIENDS: 'Vänner (ömsesidigt följande)',
  FOLLOWER_OF_CREATOR: 'Följare',
  SELF_ONLY: 'Bara jag (privat)',
}

export function privacyLabel(level: string): string {
  return PRIVACY_LABELS[level] ?? level
}

/** Terminala TikTok-tillstånd + våra ScheduledPostStatus-strängar → text ägaren förstår. */
export function publishStatusText(status: string, rawStatus: string): { text: string; kind: 'ok' | 'warn' | 'error' } {
  switch (status) {
    case 'Published':
      return { text: 'Publicerad på TikTok', kind: 'ok' }
    case 'Failed':
      return { text: 'Publiceringen misslyckades', kind: 'error' }
    case 'Publishing':
      return {
        text: rawStatus === 'SEND_TO_USER_INBOX'
          ? 'Skickad till din TikTok-inkorg'
          : 'TikTok bearbetar videon …',
        kind: 'warn',
      }
    default:
      return { text: status, kind: 'warn' }
  }
}

/**
 * Felkoder från PublishEndpoints, översatta till text ägaren kan agera på.
 * Okända koder faller igenom — en tyst standardsträng döljer nya felmoder.
 */
export function publishErrorText(code: string | undefined, detail: string | undefined): string | null {
  switch (code) {
    case 'tiktok_account_not_found':
      return 'Inget anslutet TikTok-konto hittades. Anslut kontot på Konton-sidan.'
    case 'tiktok_requires_reauth':
      return 'TikTok-anslutningen måste förnyas. Återanslut kontot på Konton-sidan.'
    case 'tiktok_token_unavailable':
      return 'TikTok-token gick inte att hämta. Återanslut kontot.'
    case 'draft_not_found':
      return 'Utkastet kunde inte hittas.'
    case 'not_a_tiktok_draft':
      return 'Bara TikTok-utkast kan publiceras här.'
    case 'draft_not_publishable':
      return 'Utkastet är redan schemalagt eller publicerat.'
    case 'draft_text_not_publishable':
      return 'Utkastets text har blockerande anmärkningar. Rätta dem först.'
    case 'media_not_found':
      return 'Den valda videon kunde inte hittas.'
    case 'publish_gate_failed':
      return 'Något i bekräftelsen saknas eller strider mot TikToks regler. Se detaljerna.'
    case 'tiktok_creator_info_failed':
      return `TikTok svarade inte på kontoinfo (${detail ?? 'okänt fel'}). Försök igen.`
    case 'tiktok_init_failed':
      return tiktokInitDetail(detail)
    case 'tiktok_published_but_unrecorded':
      return `Videon publicerades på TikTok men kunde inte sparas i dashboarden (publicerings-id ${detail ?? 'okänt'}). Publicera INTE igen — kontrollera TikTok och hör av dig så kopplar vi posten.`
    case 'tiktok_status_failed':
      return `Kunde inte hämta status från TikTok (${detail ?? 'okänt fel'}).`
    default:
      return null
  }
}

/** De vanligaste init-felkoderna från TikTok, med konkret åtgärd. */
function tiktokInitDetail(detail: string | undefined): string {
  switch (detail) {
    case 'url_ownership_unverified':
      return 'TikTok litar inte på pull-domänen ännu. Verifiera domänen under URL properties i TikTok-portalen.'
    case 'unaudited_client_can_only_post_to_private_accounts':
      return 'Appen är inte granskad ännu, så videon kan bara publiceras privat (Bara jag). Välj den synligheten.'
    case 'spam_risk_too_many_posts':
      return 'TikTok bromsar för många publiceringar. Vänta en stund och försök igen.'
    default:
      return `TikTok avvisade publiceringen (${detail ?? 'okänt fel'}).`
  }
}
